import { secureUuid } from '../uuid';
import { PROTOCOL_VERSION, type ActionPayload, type ActionSchema, type ClientMessage, type PresentationRef, type PresentationVariantRef, type PublicSeatProfile, type ServerMessage, type WireEvent } from '../protocol/wire';
import type { Description, Observation } from '../protocol/ui';
import type { SeatCredential } from './fragment';
import { assemble, choiceEligible, needsQueryCheck, validateCommand, type ChoiceView, type Draft } from '../model/choices';

export type ConnectionState = 'connecting' | 'open' | 'reconnecting' | 'closed';

export interface LogEntry {
  id: number;
  revision: number;
  kind: string;
  message: string;
}

export interface PendingCommand {
  requestId: string;
  label: string;
  revision: number;
}

/** One displayed position kept for move replay: the observation at a revision and the events that produced it. */
export interface HistoryFrame {
  revision: number;
  observation: Observation;
  /** Log entries that arrived with this revision (empty for the first position seen). */
  events: LogEntry[];
}

/** Undo availability for this seat, from snapshot metadata; null when the host does not offer undo. */
export interface UndoState { canUndo: boolean; canRedo: boolean; label: string | null }

export interface Notice {
  id: number;
  tone: 'error' | 'info';
  text: string;
  code?: string;
}

export interface SessionState {
  profiles?: PublicSeatProfile[];
  connection: ConnectionState;
  seat: string | null;
  gameId: string | null;
  matchId: string | null;
  title: string | null;
  summary: string | null;
  revision: number;
  decisionId: string | null;
  observation: Observation | null;
  description: Description | null;
  actionSchemas: ActionSchema[];
  presentation: PresentationRef | null;
  /** The player-selectable layouts the host announced; empty unless the game has two or more. */
  presentationVariants: PresentationVariantRef[];
  log: LogEntry[];
  /** The last HISTORY_LIMIT positions seen since page load, oldest first, one per revision. */
  history: HistoryFrame[];
  undo: UndoState | null;
  pending: PendingCommand | null;
  queryCheck?: { key: string; status: 'pending' | 'blocked' | 'error'; reason: string | null } | null;
  notice: Notice | null;
  fatal: string | null;
  paused: boolean;
}

export const initialSessionState: SessionState = {
  connection: 'connecting', seat: null, gameId: null, matchId: null, title: null, summary: null,
  revision: 0, decisionId: null, observation: null, description: null, actionSchemas: [],
  presentation: null, presentationVariants: [], log: [], history: [], undo: null, pending: null, notice: null, fatal: null, paused: false,
};

const LOG_LIMIT = 200;
export const HISTORY_LIMIT = 200;
const FATAL_JOIN_ERRORS = new Set(['invalid_capability', 'seat_requires_capability', 'seat_is_bot', 'unsupported_protocol']);

type Listener = (state: SessionState) => void;
export interface QueryReply { revision: number; result: unknown }
type QueryWaiter = { resolve: (value: QueryReply) => void; reject: (error: Error) => void; timer: ReturnType<typeof setTimeout> };

/**
 * One WebSocket session against the authoritative host. It never retries a command on its
 * own: after a reconnect the player sees the fresh snapshot and decides again.
 */
export class Session {
  readOnly = false;
  private socket: WebSocket | null = null;
  private state: SessionState = initialSessionState;
  private listeners = new Set<Listener>();
  private retryDelay = 500;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private stopped = false;
  private nextId = 1;
  private queries = new Map<string, QueryWaiter>();
  private checkAttempt = 0;
  private lastCheckAt = -Infinity;
  private checkingChoice: ChoiceView | null = null;

  private readonly url: string;
  private readonly credential: SeatCredential | null;
  private readonly socketFactory: (url: string) => WebSocket;

  constructor(url: string, credential: SeatCredential | null, socketFactory: (url: string) => WebSocket = u => new WebSocket(u)) {
    this.url = url;
    this.credential = credential;
    this.socketFactory = socketFactory;
  }

  get current(): SessionState { return this.state; }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.state);
    return () => this.listeners.delete(listener);
  }

  start(): void {
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    if (this.retryTimer) clearTimeout(this.retryTimer);
    this.socket?.close();
    this.update({ connection: 'closed' });
  }

  submit(action: ActionPayload, label: string): boolean {
    const s = this.state;
    if (this.readOnly || s.paused || !this.socket || s.connection !== 'open' || !s.seat || s.seat === 'spectator' || s.pending) return false;
    const requestId = newRequestId();
    this.send({ type: 'command', protocolVersion: PROTOCOL_VERSION, requestId, expectedRevision: s.revision, decisionId: s.decisionId ?? undefined, action });
    this.update({ pending: { requestId, label, revision: s.revision }, notice: null });
    return true;
  }

  /** The single completed-choice submission gate used by both table and fallback controls. */
  async submitDraft(draft: Draft, label = draft.choice.label, currentDraft: () => boolean = () => true): Promise<boolean> {
    if (this.state.queryCheck?.status === 'pending') return false;
    let action: { kind: string; data: Record<string, unknown> };
    try { action = assemble(draft); }
    catch (error) { this.checkProblem('', 'blocked', (error as Error).message); return false; }
    if (!canCheck(this.state, draft.choice) || this.readOnly || !currentDraft()) return false;
    if (!needsQueryCheck(draft)) return this.submit(action, label);
    const problem = validateCommand(action, this.state.actionSchemas);
    if (problem) { this.checkProblem('', 'blocked', problem); return false; }
    const origin = this.state, scope = draft.choice.scope ?? 'decision', key = completedActionKey(action, origin.revision, origin.seat);
    // No automatic retry or per-keystroke queries; keep manual retries below the wire rate.
    if (Date.now() - this.lastCheckAt < 150) { this.checkProblem(key, 'error', 'Wait a moment, then retry.'); return false; }
    this.lastCheckAt = Date.now();
    const attempt = ++this.checkAttempt;
    this.checkingChoice = draft.choice;
    this.update({ queryCheck: { key, status: 'pending', reason: null } });
    const stillCurrent = () => attempt === this.checkAttempt && currentDraft() && canCheck(this.state, draft.choice) && !this.readOnly && (draft.choice.scope ?? 'decision') === scope &&
      this.state.seat === origin.seat && this.state.revision === origin.revision && this.state.decisionId === origin.decisionId &&
      completedActionKey(action, this.state.revision, this.state.seat) === key;
    try {
      const reply = await this.query('check', { action });
      if (!stillCurrent()) { if (attempt === this.checkAttempt) this.invalidateChoiceCheck(); return false; }
      if (reply.revision !== origin.revision) { this.checkProblem(key, 'error', 'The check is stale. Retry against the current position.'); return false; }
      const result = reply.result as { ok?: unknown; reason?: unknown } | null;
      if (!result || result.ok !== true) {
        this.checkProblem(key, result?.ok === false ? 'blocked' : 'error', typeof result?.reason === 'string' ? result.reason : 'The move could not be checked. Retry.'); return false;
      }
      this.update({ queryCheck: null });
      this.checkingChoice = null;
      // Apply still judges legality at the exact normal command boundary.
      return stillCurrent() && this.submit(action, label);
    } catch {
      if (stillCurrent()) this.checkProblem(key, 'error', 'The check failed. Your draft is saved; retry when connected.');
      return false;
    }
  }

  invalidateChoiceCheck(): void { ++this.checkAttempt; this.checkingChoice = null; if (this.state.queryCheck) this.update({ queryCheck: null }); }
  private checkProblem(key: string, status: 'blocked' | 'error', reason: string): void { this.checkingChoice = null; this.update({ queryCheck: { key, status, reason } }); }

  /** Sends an undo or redo request with the current freshness context (host undo contract). */
  control(type: 'undo' | 'redo'): boolean {
    const s = this.state;
    if (this.readOnly || s.paused || !this.socket || s.connection !== 'open' || !s.seat || s.seat === 'spectator' || s.pending) return false;
    if (type === 'undo' ? !s.undo?.canUndo : !s.undo?.canRedo) return false;
    const requestId = newRequestId();
    this.send({ type, protocolVersion: PROTOCOL_VERSION, requestId, expectedRevision: s.revision, decisionId: s.decisionId ?? undefined });
    this.update({ pending: { requestId, label: type === 'undo' ? 'Undo' : 'Redo', revision: s.revision }, notice: null });
    return true;
  }

  query(kind: string, data: Record<string, unknown>): Promise<QueryReply> {
    if (!this.socket || this.state.connection !== 'open') return Promise.reject(new Error('not_connected'));
    const requestId = newRequestId();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => { this.queries.delete(requestId); reject(new Error('query_timeout')); }, 10000);
      this.queries.set(requestId, { resolve, reject, timer });
      this.send({ type: 'query', protocolVersion: PROTOCOL_VERSION, requestId, query: { kind, data } });
    });
  }

  dismissNotice(): void { this.update({ notice: null }); }

  private connect(): void {
    const socket = this.socketFactory(this.url);
    this.socket = socket;
    socket.onopen = () => {
      this.retryDelay = 500;
      const join: ClientMessage = { type: 'join', protocolVersion: PROTOCOL_VERSION };
      if (this.credential) join.seatToken = this.credential.token;
      this.send(join);
    };
    socket.onmessage = event => {
      try { this.handle(JSON.parse(String(event.data)) as ServerMessage); }
      catch { /* a malformed message is ignored; the next snapshot replaces state */ }
    };
    socket.onclose = () => {
      if (this.socket !== socket) return;
      this.socket = null;
      for (const waiter of this.queries.values()) { clearTimeout(waiter.timer); waiter.reject(new Error('disconnected')); }
      this.queries.clear();
      if (this.stopped || this.state.fatal) { this.update({ connection: 'closed', pending: null }); return; }
      this.update({ connection: 'reconnecting', pending: null });
      this.retryTimer = setTimeout(() => this.connect(), this.retryDelay);
      this.retryDelay = Math.min(this.retryDelay * 2, 8000);
    };
  }

  private handle(message: ServerMessage): void {
    switch (message.type) {
      case 'joined':
        this.update({
          ...(this.state.matchId !== (message.matchId ?? null) || this.state.seat !== (message.seat ?? 'spectator') ? { observation: null, history: [], log: [], pending: null, undo: null } : {}),
          connection: 'open', seat: message.seat ?? 'spectator', gameId: message.gameId ?? null, matchId: message.matchId ?? null,
          title: message.title ?? null, summary: message.summary ?? null, description: (message.description as Description | null) ?? null,
          actionSchemas: message.actionSchemas ?? [], presentation: message.presentation ?? null, presentationVariants: message.presentationVariants ?? [], profiles: message.profiles ?? [],
          revision: message.revision, decisionId: message.decisionId ?? null,
        });
        break;
      case 'snapshot': {
        // A reconnect may resend the latest revision with its events; log each revision once.
        const known = this.state.log.some(l => l.revision === message.revision);
        const log = message.events?.length && !known ? this.appendLog(message.events, message.revision) : this.state.log;
        const observation = message.observation as Observation;
        const undo = message.canUndo == null && message.canRedo == null ? null
          : { canUndo: !!message.canUndo, canRedo: !!message.canRedo, label: message.undoLabel ?? null };
        this.update({ observation, revision: message.revision, decisionId: message.decisionId ?? null, log, history: recordFrame(this.state.history, message.revision, observation, log), undo, paused: !!message.paused, profiles: message.profiles ?? this.state.profiles });
        break;
      }
      case 'result':
        if (this.state.pending?.requestId === message.requestId) {
          this.update({
            pending: null,
            notice: message.accepted ? null : { id: this.nextId++, tone: 'error', text: '', code: message.error ?? undefined },
          });
        }
        break;
      case 'queryResult': {
        const waiter = message.requestId ? this.queries.get(message.requestId) : undefined;
        if (waiter && message.requestId) { this.queries.delete(message.requestId); clearTimeout(waiter.timer); waiter.resolve({ revision: message.revision, result: message.result }); }
        break;
      }
      case 'error': {
        const waiter = message.requestId ? this.queries.get(message.requestId) : undefined;
        if (waiter && message.requestId) { this.queries.delete(message.requestId); clearTimeout(waiter.timer); waiter.reject(new Error(message.error ?? 'error')); break; }
        if (message.error && FATAL_JOIN_ERRORS.has(message.error) && this.state.connection !== 'open') {
          this.update({ fatal: message.error, connection: 'closed' });
          this.socket?.close();
          break;
        }
        const clearsPending = this.state.pending && (!message.requestId || message.requestId === this.state.pending.requestId);
        this.update({ notice: { id: this.nextId++, tone: 'error', text: '', code: message.error ?? undefined }, pending: clearsPending ? null : this.state.pending });
        break;
      }
      default:
        break;
    }
  }

  private appendLog(events: WireEvent[], revision: number): LogEntry[] {
    const added = events.map(e => ({ id: this.nextId++, revision, kind: e.kind, message: e.message ?? e.kind }));
    const log = [...this.state.log, ...added];
    return log.length > LOG_LIMIT ? log.slice(log.length - LOG_LIMIT) : log;
  }

  private send(message: ClientMessage): void { this.socket?.send(JSON.stringify(message)); }

  private update(patch: Partial<SessionState>): void {
    if (patch.revision !== undefined && patch.revision !== this.state.revision || patch.decisionId !== undefined && patch.decisionId !== this.state.decisionId || patch.seat !== undefined && patch.seat !== this.state.seat || patch.observation && this.checkingChoice && !canCheck({ ...this.state, ...patch }, this.checkingChoice) || patch.connection && patch.connection !== 'open' || patch.paused === true) {
      ++this.checkAttempt; this.checkingChoice = null; patch.queryCheck = null;
    }
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener(this.state);
  }
}

function canCheck(state: SessionState, choice: ChoiceView): boolean {
  const o = state.observation, seat = state.seat;
  return state.connection === 'open' && !state.pending && !state.paused && !!o && choiceEligible({ selfSeat: seat, finished: o.finished, actor: o.actor,
    decisionOwners: Array.isArray(o.decisionOwners) ? o.decisionOwners as string[] : undefined, auxiliaryActions: o.auxiliaryActions }, choice);
}

/** Stable typed JSON identity: strings remain strings, including exact 19-digit decimals. */
export function completedActionKey(action: ActionPayload, revision: number, seat: string | null): string {
  const canonical = (value: unknown): unknown => Array.isArray(value) ? value.map(canonical) : value && typeof value === 'object'
    ? Object.fromEntries(Object.entries(value).sort(([a], [b]) => a.localeCompare(b)).map(([k, v]) => [k, canonical(v)])) : value;
  return JSON.stringify([seat, revision, action.kind, canonical(action.data ?? {})]);
}

/** Adds or replaces the frame for a revision (a reconnect may resend one), keeping the newest HISTORY_LIMIT. */
export function recordFrame(history: HistoryFrame[], revision: number, observation: Observation, log: LogEntry[]): HistoryFrame[] {
  const events = log.filter(l => l.revision === revision);
  const next = [...history.filter(f => f.revision !== revision), { revision, observation, events }].sort((a, b) => a.revision - b.revision);
  return next.length > HISTORY_LIMIT ? next.slice(next.length - HISTORY_LIMIT) : next;
}

function newRequestId(): string {
  return secureUuid();
}

export function socketUrl(location: Location): string {
  const game = /^\/rooms\/([^/]+)\/games\/([^/]+)/.exec(location.pathname);
  const path = game ? `/api/rooms/${game[1]}/games/${game[2]}/ws` : '/ws';
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}${path}`;
}
